import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { saveSong, loadSongs, removeSongFromDB } from '../utils/songStorage';
import { useScoreboard } from './ScoreboardContext';


const MediaContext = createContext();

export const MediaProvider = ({ children, isGameRunning }) => {
  const { isEntertainmentEnabled } = useScoreboard();
  const [songs, setSongs] = useState(Array(10).fill(null));
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSongIndex, setCurrentSongIndex] = useState(null);
  const [currentSong, setCurrentSong] = useState(null);
  
  // Use refs to prevent race conditions and ensure singleton behavior
  const audioInstanceRef = useRef(null);
  const isProcessingRef = useRef(false);

  const [youtubeVideos, setYoutubeVideos] = useState(() => {
    const saved = localStorage.getItem('youtube-videos');
    return saved ? JSON.parse(saved) : Array(4).fill().map(() => ({ url: '', loop: false }));
  });

  const [youtubePlaylistUrl, setYoutubePlaylistUrl] = useState(() => {
    const saved = localStorage.getItem('youtube-playlist-url');
    return saved || '';
  });
  const lastCtrlPressRef = useRef(null);

  // Handle game state changes
  useEffect(() => {
    if (isGameRunning) {
      // Stop music and close overlay when game starts
      if (isPlaying) {
        stopAudio();
      }

    }
  }, [isGameRunning]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isEntertainmentEnabled) return;

      // Handle Ctrl key press
      if (e.key === 'Control') {
        const now = Date.now();
        
        // Check if this is a second press within 500ms
        if (lastCtrlPressRef.current && (now - lastCtrlPressRef.current) <= 500) {
          // Dispatch a custom event that Scoreboard will listen for
          window.dispatchEvent(new CustomEvent('toggleMusicOverlay'));
          lastCtrlPressRef.current = null;
        } else {
          lastCtrlPressRef.current = now;
          // Set a timeout to reset the state if no second press occurs
          setTimeout(() => {
            lastCtrlPressRef.current = null;
          }, 500);
        }
      }
      
      // Handle number keys with Ctrl
      if (e.ctrlKey) {
        let num = parseInt(e.key);
        if (num >= 0 && num <= 9) {
          e.preventDefault();
          // Dispatch event to close music overlay if it's open
          window.dispatchEvent(new CustomEvent('toggleMusicOverlay', { detail: { action: 'close' } }));
          
          // Convert 0 to index 9 (for Ctrl+0)
          const index = num === 0 ? 9 : num - 1;
          
          // If the song exists at this index
          if (songs[index]) {
            // If this is the current playing song, toggle play/pause
            if (currentSongIndex === index) {
              if (isPlaying) {
                stopAudio();
              } else {
                playAudio(songs[index], index);
              }
            } else {
              // If it's a different song, play it
              playAudio(songs[index], index);
            }
          }
        }
      }
    };
    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);


    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);

    };
  }, [songs, currentSongIndex, isPlaying, isGameRunning, isEntertainmentEnabled]);

  // Load initial state
  useEffect(() => {
    const savedState = localStorage.getItem('media-state');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      setCurrentSongIndex(parsed.currentSongIndex);
      setIsPlaying(false); // Always start paused
    }
    
    // Load songs and restore current song if needed
    loadSongs().then(loadedSongs => {
      setSongs(loadedSongs);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.currentSongIndex !== null && loadedSongs[parsed.currentSongIndex]) {
          setCurrentSong(loadedSongs[parsed.currentSongIndex]);
        }
      }
    });
  }, []);

  // Save videos to localStorage
  useEffect(() => {
    localStorage.setItem('youtube-videos', JSON.stringify(youtubeVideos));
  }, [youtubeVideos]);

  // Save playlist URL to localStorage
  useEffect(() => {
    localStorage.setItem('youtube-playlist-url', youtubePlaylistUrl);
  }, [youtubePlaylistUrl]);

  // Migrate old youtube-video-url to new format if needed
  useEffect(() => {
    const oldUrl = localStorage.getItem('youtube-video-url');
    if (oldUrl) {
      const newVideos = [...youtubeVideos];
      newVideos[0] = { url: oldUrl, loop: false };
      setYoutubeVideos(newVideos);
      localStorage.removeItem('youtube-video-url');
    }
  }, []);

  // Save state to localStorage
  useEffect(() => {
    const state = {
      isPlaying,
      currentSongIndex,
    };

    localStorage.setItem('media-state', JSON.stringify(state));
  }, [isPlaying, currentSongIndex]);

  // Handle actual audio playback
  const handlePlay = async (audio) => {
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  };

  const playAudio = async (song, index) => {
    if (!isEntertainmentEnabled || isGameRunning) {
      console.log('Cannot play audio while game is running');
      return;
    }

    // Strict singleton pattern - prevent any concurrent audio operations
    if (isProcessingRef.current) {
      console.log('Audio operation already in progress, ignoring request');
      return;
    }

    isProcessingRef.current = true;

    try {
      // Always stop any existing audio first
      if (audioInstanceRef.current) {
        console.log('Stopping existing audio instance');
        audioInstanceRef.current.pause();
        audioInstanceRef.current.currentTime = 0;
        audioInstanceRef.current = null;
      }

      // Reset all states
      setIsPlaying(false);
      setCurrentSong(null);
      setCurrentSongIndex(null);
      setCurrentAudio(null);

      // Create the single audio instance
      const audio = new Audio();
      audioInstanceRef.current = audio;
      
      // Set up event handlers
      audio.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        if (audioInstanceRef.current === audio) {
          audioInstanceRef.current = null;
          setIsPlaying(false);
          setCurrentSongIndex(null);
          setCurrentSong(null);
          setCurrentAudio(null);
        }
      });

      audio.addEventListener('ended', () => {
        if (audioInstanceRef.current === audio) {
          audioInstanceRef.current = null;
          setIsPlaying(false);
          setCurrentSong(null);
          setCurrentSongIndex(null);
          setCurrentAudio(null);
        }
      });

      // Set source
      audio.src = song.data;

      // Wait for the audio to be ready
      await new Promise((resolve, reject) => {
        const onCanPlay = () => {
          audio.removeEventListener('canplaythrough', onCanPlay);
          audio.removeEventListener('error', onError);
          resolve();
        };
        const onError = (e) => {
          audio.removeEventListener('canplaythrough', onCanPlay);
          audio.removeEventListener('error', onError);
          reject(e);
        };
        audio.addEventListener('canplaythrough', onCanPlay, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });

      // Verify this is still our current audio instance
      if (audioInstanceRef.current !== audio) {
        console.log('Audio instance was replaced during loading');
        return;
      }

      // Update states
      setCurrentSong(song);
      setCurrentSongIndex(index);
      setCurrentAudio(audio);
      
      // Play the audio
      console.log('Attempting to play audio...');
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        // Verify this is still our current audio instance after play
        if (audioInstanceRef.current === audio) {
          console.log('Audio started playing successfully');
          setIsPlaying(true);
        }
      } else {
        throw new Error('Play promise was undefined');
      }
    } catch (error) {
      console.error('Error in playAudio:', error);
      // Clean up on error
      if (audioInstanceRef.current) {
        audioInstanceRef.current.pause();
        audioInstanceRef.current = null;
      }
      setIsPlaying(false);
      setCurrentSongIndex(null);
      setCurrentSong(null);
      setCurrentAudio(null);
    } finally {
      isProcessingRef.current = false;
    }
  };

  // pauseAudio wurde entfernt, da stopAudio mit Fadeout verwendet wird

  const stopAudio = (fadeOutDuration = 500) => {
    console.log('stopAudio aufgerufen');
    
    // Reset processing state
    isProcessingRef.current = false;
    
    const audioToStop = audioInstanceRef.current;
    
    if (audioToStop) {
      // For immediate stops (fadeOutDuration = 0), don't fade
      if (fadeOutDuration === 0) {
        audioToStop.pause();
        audioToStop.currentTime = 0;
        audioInstanceRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentSongIndex(null);
        setCurrentSong(null);
        return;
      }

      // Speichere die aktuelle Lautstärke
      const originalVolume = audioToStop.volume;
       
      // Prüfe, ob das Audio-Element gültig ist
      if (typeof audioToStop.volume === 'undefined') {
        console.error('Audio-Element hat keine volume-Eigenschaft');
        // Fallback zur alten Methode
        audioToStop.pause();
        audioToStop.currentTime = 0;
        audioInstanceRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentSongIndex(null);
        setCurrentSong(null);
        return;
      }
      
      // Erstelle einen Fadeout-Effekt
      const fadeSteps = 20; // Anzahl der Schritte für den Fadeout
      const fadeStepTime = fadeOutDuration / fadeSteps;
      const volumeStep = originalVolume / fadeSteps;
      
      // Führe den Fadeout durch
      let currentStep = 0;
      const fadeInterval = setInterval(() => {
        currentStep++;
        
        // Check if this is still our audio instance
        if (audioInstanceRef.current !== audioToStop) {
          clearInterval(fadeInterval);
          return;
        }
        
        if (currentStep < fadeSteps) {
          // Reduziere die Lautstärke schrittweise
          const newVolume = Math.max(0, originalVolume - (volumeStep * currentStep));
          
          try {
            audioToStop.volume = newVolume;
          } catch (error) {
            console.error('Fehler beim Setzen der Lautstärke:', error);
          }
        } else {
          clearInterval(fadeInterval);
          audioToStop.pause();
          audioToStop.currentTime = 0;
          try {
            audioToStop.volume = originalVolume; // Setze die Lautstärke zurück
          } catch (error) {
            console.error('Fehler beim Zurücksetzen der Lautstärke:', error);
          }
          
          // Only clear if this is still our current instance
          if (audioInstanceRef.current === audioToStop) {
            audioInstanceRef.current = null;
            setCurrentAudio(null);
            setIsPlaying(false);
            setCurrentSongIndex(null);
            setCurrentSong(null);
          }
        }
      }, fadeStepTime);
    } else {
      console.log('Kein currentAudio vorhanden, nichts zu stoppen');
      // Still reset states in case they're out of sync
      setIsPlaying(false);
      setCurrentSongIndex(null);
      setCurrentSong(null);
    }
  };

  const handleSongUpload = async (index, file) => {
    if (!isEntertainmentEnabled) return;
    if (!file || !file.type.startsWith('audio/')) {
      alert('Please upload an MP3 file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const song = {
        name: file.name,
        data: e.target.result,
        type: file.type
      };
      await saveSong(index, song);
      const newSongs = [...songs];
      newSongs[index] = song;
      setSongs(newSongs);
    };
    reader.readAsDataURL(file);
  };

  const removeSong = async (index) => {
    if (!isEntertainmentEnabled) return;
    // If this is the current song, stop it
    if (index === currentSongIndex) {
      stopAudio();
    }
    await removeSongFromDB(index);
    const newSongs = [...songs];
    newSongs[index] = null;
    setSongs(newSongs);
  };

  const value = {
    songs,
    isPlaying,
    currentSongIndex,

    playAudio,
    stopAudio,
    handleSongUpload,
    removeSong,
    youtubeVideos,
    setYoutubeVideos,
    youtubePlaylistUrl,
    setYoutubePlaylistUrl,
  };

  return (
    <MediaContext.Provider value={value}>
      {children}
    </MediaContext.Provider>
  );
};

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (!context) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
};
